import { useState, useEffect, useCallback } from 'react';
import { Plus, Filter } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import ApiKeysList, { ApiKey } from '@/components/api-keys/ApiKeysList';
import CreateKeyModal from '@/components/api-keys/CreateKeyModal';
import KeyFilters from '@/components/api-keys/KeyFilters';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { apiKeyService } from '@/services/apiKeyService';
import { useRoleAccess } from '@/hooks/useRoleAccess';

const ApiKeys = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    environment: 'all',
    status: 'all',
    role: 'all',
  });
  const { toast } = useToast();
  const { canGenerateApiKeys, isViewer } = useRoleAccess();

  const loadApiKeys = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const apiKeys = await apiKeyService.getApiKeys();
      setKeys(apiKeys);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load API keys';
      setError(errorMessage);
      toast({
        title: "Error loading API keys",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Load API keys on component mount
  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleCreateKey = async (
    name: string, 
    scopes: string[], 
    environment: string, 
    expiresAt: string | null,
    role: string,
    ttlDays: number
  ) => {
    try {
      const newKey = await apiKeyService.createApiKey(
        name,
        scopes,
        environment,
        expiresAt,
        role,
        ttlDays
      );

      setKeys([newKey, ...keys]);
      
      toast({
        title: "API Key created",
        description: `'${name}' has been successfully created.`,
      });

      setNewlyCreatedKey(newKey.token);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create API key';
      toast({
        title: "Error creating API key",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleRevokeKey = async (id: string) => {
    try {
      await apiKeyService.revokeApiKey(id);
      
      // Update the local state to reflect the revoked status
      setKeys(keys.map(key => 
        key.id === id 
          ? { ...key, status: 'revoked' } 
          : key
      ));

      toast({
        title: "API Key revoked",
        description: "The API key has been revoked successfully.",
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to revoke API key';
      toast({
        title: "Error revoking API key",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  // Apply filters
  const filteredKeys = keys.filter(key => {
    if (filters.environment !== 'all' && key.environment !== filters.environment) return false;
    if (filters.status !== 'all' && key.status !== filters.status) return false;
    if (filters.role !== 'all' && key.role !== filters.role) return false;
    return true;
  });

  // Show loading state
  if (isLoading) {
    return (
      <PageLayout 
        title="API Keys" 
        description="Manage API keys for your integrations"
        showBackToDashboard
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading API keys...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Show error state
  if (error && keys.length === 0) {
    return (
      <PageLayout 
        title="API Keys" 
        description="Manage API keys for your integrations"
        showBackToDashboard
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 mb-4">Error: {error}</p>
            <Button onClick={loadApiKeys} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout 
        title="API Keys" 
        description="Manage API keys for your integrations"
        showBackToDashboard
      >
        {isViewer && (
          <div className="mb-6 p-4 bg-yellow-500/10 rounded-lg flex gap-3 items-start border border-yellow-500/30">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-yellow-500 rounded-full"></div>
              <span className="text-sm font-medium">Viewer Access</span>
            </div>
            <p className="text-sm text-muted-foreground">You can view existing API keys but cannot generate new ones. Contact your team admin for API key creation.</p>
          </div>
        )}
        
        <div className="flex flex-wrap justify-between mb-6 gap-4">
          <Button
            variant="outline"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="gap-2"
          >
            <Filter size={16} />
            Filters
          </Button>
          
          {canGenerateApiKeys && (
            <Button 
              onClick={() => setIsCreateModalOpen(true)}
              className="btn-primary"
            >
              <Plus size={16} className="mr-2" />
              Generate New Key
            </Button>
          )}
        </div>

        {isFilterOpen && (
          <div className="mb-6 w-full">
            <KeyFilters 
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </div>
        )}

        <div className="w-full overflow-x-auto">
          <ApiKeysList 
            keys={filteredKeys} 
            onRevoke={handleRevokeKey} 
          />
        </div>
      </PageLayout>

      <CreateKeyModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setNewlyCreatedKey(null);
        }}
        onCreateKey={handleCreateKey}
        newlyCreatedKey={newlyCreatedKey}
      />
    </>
  );
};

export default ApiKeys;
